import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import Api from '../../services/api.service';
import H1 from '../../components/Titles/h1';
import Link from '../../components/Links/link';

import Loading from '../../components/Loading/loading';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import FullWidthImage from '../../components/Images/image-full-width';

import Button from '../../components/Buttons/button';
import DescriptionSplit from '../../components/Description/description-split';
import TitlePage from '../../components/title-page';
import Select from '../../components/Inputs/select';
import H4 from '../../components/Titles/h4';
import H5 from '../../components/Titles/h5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useCart } from './../../contexts/cart-context';
import CarouselImagesProduct from './carousel-images';
import Helper from '../../services/helper.service';
import CardProduct from '../Shop/card-product'; // <--- IMPORT CARD PRODUCT

export default function Product() {
    const { addToCart } = useCart();
    const { slug } = useParams();
    const { t, i18n } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState(null);
    const [mainImage, setMainImage] = useState(undefined);

    // --- NEW STATE FOR SUGGESTIONS ---
    const [suggestedProducts, setSuggestedProducts] = useState([]);
    // ---------------------------------

    const [selectedModelIndex, setSelectedModelIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);

    const handleImageClick = (imageUrl) => {
        setMainImage(imageUrl);
    };

    const buildImageUrl = (src) => {
        if (!src) return '';
        if (src.startsWith('http://') || src.startsWith('https://')) return src;
        return `https://api.lartbanne.com/uploads/artbanne/${src}`;
    };

    useEffect(() => {
        setLoading(true);
        Api.getProduct(slug).then((response) => {
            const res = response.data;
            if (res.status) {
                const rawProduct = res.data;

                const p = {
                    ...rawProduct,
                    imageSrc: buildImageUrl(rawProduct.imageSrc),
                    imagesList: (rawProduct.imagesList || []).map(buildImageUrl),
                };

                setProduct(p);
                setMainImage(p.imageSrc);
                setSelectedModelIndex(0);

                const firstModelStock = p.modelProduct?.[0]?.quantity || 0;
                setQuantity(Number(firstModelStock) > 0 ? 1 : 0);
            } else {
                console.log('error');
            }
            setLoading(false);
        });
    }, [slug]);

    // 2. Fetch Suggested Products (NEW)
    useEffect(() => {
        Api.getSuggestedProducts().then((response) => {
            const res = response.data;
            if (res.status && res.data) {
                // Filter logic:
                // 1. Remove the current product (by slug)
                // 2. Shuffle (optional, here we just take the first ones)
                // 3. Slice to get only 4 items
                const suggestions = res.data
                    .filter(item => item.slug !== slug) // Don't suggest the current product
                    .slice(0, 4); // Take only 4

                setSuggestedProducts(suggestions);
            }
        });
    }, [slug]);

    const currentModel = useMemo(() => {
        if (!product?.modelProduct || !product.modelProduct.length) return null;
        return product.modelProduct[selectedModelIndex] || product.modelProduct[0];
    }, [product, selectedModelIndex]);

    const stock = Number(currentModel?.quantity || 0);
    const isOutOfStock = stock <= 0;

    const formatPrice = (n) => {
        const num = Number(n);
        if (!Number.isFinite(num)) return '';
        return `${num.toFixed(2)} EUR`;
    };

    const showPublicPrice =
        currentModel &&
        currentModel.publicPriceTTC != null &&
        currentModel.publicPriceTTC !== '' &&
        Number(currentModel.publicPriceTTC) > 0 &&
        Number(currentModel.publicPriceTTC) !== Number(currentModel.priceTTC);

    const numberOptions = () => {
        if (!currentModel) return [];
        if (isOutOfStock) return [{ value: '0', label: '0' }];
        const max = Number(currentModel.quantity) || 1;
        return Array.from({ length: max }, (_, index) => ({
            value: `${index + 1}`,
            label: `${index + 1}`,
        }));
    };

    const hasSingleVintage = product?.modelProduct?.length === 1;

    const millesimeOptions = () => {
        if (!product?.modelProduct) return [];
        return product.modelProduct.map((m, index) => ({
            value: `${index}`,
            label: m.vintage ? `${m.vintage}` : (m.model || `${index + 1}`)
        }));
    };

    const handleSelectChangeQuantity = (event) => {
        setQuantity(Number(event.target.value));
    };

    const handleSelectChangeMillesime = (event) => {
        const index = Number(event.target.value);
        setSelectedModelIndex(index);
        const newModel = product.modelProduct[index];
        const newStock = Number(newModel?.quantity || 0);
        setQuantity(newStock > 0 ? 1 : 0);
    };

    const handleAddToCart = () => {
        if (!product || !currentModel || isOutOfStock) return;
        addToCart({
            slug: product.slug,
            reference: currentModel.reference,
            quantity: quantity
        });
        Helper.toast(t('cart.addedToCart', { product: productName, quantity: quantity, }), "success");
    };

    if (loading) {
        return (
            <div className="bg-backgroundColor">
                <div className="m-auto max-w-6xl w-5/6 pt-14">
                    <Loading />
                </div>
            </div>
        );
    }

    if (!product || !currentModel) {
        return null;
    }

    const productName =
        (product.name && product.name[i18n.language]) ||
        (product.name && product.name.fr) ||
        '';

    return (
        <div className='bg-backgroundColor'>
            {product && (
                <Helmet>
                    <title>
                        L&apos;Artbanne - {t('product.pageTitle')} - {productName}
                    </title>
                </Helmet>
            )}

            <div className='m-auto max-w-6xl w-5/6 pt-14'>
                <TitlePage
                    title={t('product.welcomeMessage')}
                    description={t('product.description')}
                />

                <div>
                    <div className='flex flex-col md:flex-row gap-12 md:gap-20 py-12'>
                        {/* Image Column */}
                        <div className='md:w-1/3 w-full'>
                            <div className="mb-4">
                                <Link
                                    link="/"
                                    text={t('inputs.back')}
                                    textColor="main"
                                    className="text-sm flex items-center gap-2"
                                    disableHoverEffect={true}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} className="text-main text-xs" />
                                </Link>
                            </div>

                            <FullWidthImage
                                url={mainImage}
                                alt={`${t('product.alt')}${productName}`}
                                className='shadow bg-white'
                            />
                            <CarouselImagesProduct
                                listOfImages={(product.imagesList || []).map(buildImageUrl)}
                                onImageClick={handleImageClick}
                            />
                        </div>

                        {/* Product Info Column */}
                        <div className='md:w-2/3 w-full'>
                            <H1 title={productName} />

                            <div className='mt-8 shadow bg-white px-6 py-4 flex flex-col md:flex-row items-stretch md:items-center gap-4'>
                                <div className='flex flex-col'>
                                    <span className='text-xl md:text-2xl font-bold text-main block'>
                                        {formatPrice(currentModel.priceTTC)}
                                    </span>
                                    {showPublicPrice && (
                                        <span className='text-xs md:text-sm font-semibold text-borderColor line-through block mt-0.5'>
                                            {formatPrice(currentModel.publicPriceTTC)}
                                        </span>
                                    )}
                                </div>

                                <div className='flex items-center gap-2'>
                                    <span className='text-xs text-gray-500 uppercase tracking-wide'>
                                        {t('product.vintageLabel')}
                                    </span>
                                    {hasSingleVintage ? (
                                        <span className='text-sm md:text-base font-semibold'>
                                            {currentModel.vintage
                                                ? currentModel.vintage
                                                : (currentModel.model || '')}
                                        </span>
                                    ) : (
                                        <div className='min-w-[110px]'>
                                            <Select
                                                label={t('product.vintageLabel')}
                                                options={millesimeOptions()}
                                                value={String(selectedModelIndex)}
                                                onChange={handleSelectChangeMillesime}
                                                displayLabel={false}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className='flex items-center gap-2'>
                                    <span className='text-xs text-gray-500 uppercase tracking-wide'>
                                        {t('product.quantityLabel')}
                                    </span>
                                    <div className='min-w-[80px]'>
                                        <Select
                                            label={t('product.quantityLabel')}
                                            options={numberOptions()}
                                            value={String(quantity)}
                                            onChange={handleSelectChangeQuantity}
                                            displayLabel={false}
                                            disabled={isOutOfStock}
                                            className={isOutOfStock ? "opacity-50 cursor-not-allowed" : ""}
                                        />
                                    </div>
                                </div>

                                <div className='md:ml-auto'>
                                    <Button
                                        text={isOutOfStock ? (t('product.outOfStock') || "Unavailable") : t('inputs.addToCart')}
                                        onClick={handleAddToCart}
                                        disabled={isOutOfStock}
                                        className={isOutOfStock ? "!bg-gray-400 !cursor-not-allowed opacity-60" : ""}
                                    />
                                </div>
                            </div>

                            <div className='mt-8'>
                                <DescriptionSplit
                                    description={
                                        (product.description && product.description[i18n.language]) ||
                                        (product.description && product.description.fr) ||
                                        ''
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- SUGGESTED PRODUCTS SECTION --- */}
                <div className='pb-10'>
                    <div className='w-full flex justify-between items-center mb-6'>
                        <H4
                            title={t('product.suggested')}
                            fontWeight='bold'
                            className='w-fit'
                        />

                        <Link
                            link="/"
                            children={
                                <div className='flex gap-1 items-center'>
                                    <H5
                                        title={t('product.seeMore')}
                                        fontWeight='bold'
                                        theme='main'
                                    />
                                    <FontAwesomeIcon
                                        icon={faChevronRight}
                                        className='text-main'
                                    />
                                </div>
                            }
                        />
                    </div>

                    {/* Display the Grid of 4 Products */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                        {suggestedProducts.map((p) => (
                            <div key={p.slug || p._id} className="h-full">
                                <Link link={`/product/${p.slug}`} className="block h-full">
                                    <CardProduct product={p} />
                                </Link>
                            </div>
                        ))}
                    </div>

                    {suggestedProducts.length === 0 && (
                        <p className="text-gray-400 text-sm text-center italic">
                            {/* Optional: Message if no suggestions found */}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}